import React, { createContext, useContext, useState } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => sessionStorage.getItem('udesign_admin_auth') === 'true'
  );
  const [token, setToken] = useState<string>(
    () => sessionStorage.getItem('udesign_admin_token') || ''
  );

  const login = async (password: string): Promise<{ success: boolean; error?: string }> => {
    if (!password || !password.trim()) return { success: false, error: 'Password required' };
    try {
      const res = await fetch('/api/status-page/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        sessionStorage.setItem('udesign_admin_auth', 'true');
        sessionStorage.setItem('udesign_admin_token', data.token);
        setIsAuthenticated(true);
        setToken(data.token);
        return { success: true };
      }
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error || 'Invalid password' };
    } catch {
      return { success: false, error: 'Connection failed' };
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch('/api/status-page/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        });
      } catch {}
    }
    sessionStorage.removeItem('udesign_admin_auth');
    sessionStorage.removeItem('udesign_admin_token');
    setIsAuthenticated(false);
    setToken('');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
